import React from "react";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { SortingOrder } from "./constants";
import MuiDataGridToolBar from "./components/MuiDataGridToolBar";
import StyledDataGrid from "../../theme/extensions/StyledDataGrid";
import NoTableData from "../../global-layout/NoTableData";
import SkeletonLoadingDataGrid from "../SkeletonLoadingDataGrid";
import { ShiDataGridProps } from "../../interfaces/ShiDataGridInterface";
import DownloadIcon from "@mui/icons-material/Download";
import LineWeightIcon from "@mui/icons-material/LineWeight";
import { Box } from "@mui/material";

const MuiDataGrid: React.FC<ShiDataGridProps> = ({
  loading = false,
  gridColsDef,
  gridRows,
  hideSelectAllCheckBox = false,
  hideToolbarCheckbox = false,
  onPageChangeHandler,
  onSortChangeHandler,
  adjustRowHeight = true,
  handleRowSelection,
  rowExpandLookup,
  sortingOrder = [SortingOrder.Descending, SortingOrder.Ascending, null],
  saveUserConfig = false,
  gridId = undefined,
  disableRowSelectionOnClick = true,
  handleTableReset,
  customToolbar,
  showToolbar,
  pagination = true,
  containerStyle,
  customTheme,
  rowDensityMenu,
  exportMenu,
  viewSelect,
  hideNoTableDataSuggestions,
  hideRowHoverBackground,
  customNoDataMessage,
  userPrefHook,
  treeData,
  apiRef,
  customNoDataOverlay,
  customNoDataOverlayProps,
  customFooter,
  autoHeight = true,
  slots: externalSlots,
  slotProps: externalSlotProps,
  sx: externalSx,
  ...props
}) => {
  const localApiRef = useGridApiRef();
  apiRef ??= localApiRef;
  const shouldShowToolbar =
    showToolbar ??
    Boolean(customToolbar || saveUserConfig || rowDensityMenu || exportMenu || viewSelect);
  const {
    handleResetTableView,
    isDefaultView,
    initialState,
    columns,
    defaultInitialState,
    userConfigLoading,
    dataGridTreeDataProps,
  } = userPrefHook({
    gridId,
    apiRef,
    gridColsDef,
    rowExpandLookup,
    treeData,
    saveUserConfig,
  });

  const customNoTableData = () => {
    return (
      <NoTableData
        hideSuggestions={hideNoTableDataSuggestions}
        noDataMessage={customNoDataMessage}
      />
    );
  };

  const getSx = () => {
    let sx: Record<string, unknown> = {
      "& .MuiCheckbox-root.Mui-checked:not(.MuiCheckbox-indeterminate) svg": {
        color: "primary.main",
      },
      "& .MuiCheckbox-root.MuiCheckbox-indeterminate svg": {
        color: "primary.main",
      },
    };

    if (hideSelectAllCheckBox) {
      sx = {
        ...sx,
        "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
          {
            display: "none",
          },
      };
    }
    if (hideRowHoverBackground) {
      sx = {
        ...sx,
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "transparent",
        },
      };
    }
    if (gridRows?.length === 0) {
      sx = {
        ...sx,
        "& .MuiDataGrid-virtualScroller": {
          overflow: "hidden",
        },
        "& .MuiDataGrid-scrollbar--horizontal": {
          visibility: "hidden",
        },
      };
    }

    return sx;
  };

  return (
    <Box sx={{ width: "100%", ...containerStyle }}>
      <StyledDataGrid
        {...dataGridTreeDataProps}
        key={gridId}
        sx={[getSx(), externalSx]}
        autoHeight={autoHeight && gridRows.length > 0}
        apiRef={apiRef}
        loading={loading || userConfigLoading}
        pagination={pagination}
        showToolbar={shouldShowToolbar}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        columns={columns}
        rows={gridRows}
        paginationMode={props.paginationMode ?? "client"}
        onRowSelectionModelChange={handleRowSelection}
        disableColumnFilter={props.disableColumnFilter ?? true}
        onPaginationModelChange={onPageChangeHandler}
        onSortModelChange={onSortChangeHandler}
        adjustRowHeight={adjustRowHeight}
        getRowHeight={() => {
          return adjustRowHeight ? "auto" : null;
        }}
        initialState={defaultInitialState}
        pageSizeOptions={[15, 25, 50]}
        customTheme={customTheme}
        localeText={{
          toolbarDensity: <LineWeightIcon fontSize={"small"} />,
          toolbarExport: <DownloadIcon fontSize={"small"} />,
        }}
        slots={{
          footer: customFooter,
          loadingOverlay: SkeletonLoadingDataGrid,
          toolbar: MuiDataGridToolBar,
          noRowsOverlay: customNoDataOverlay ?? customNoTableData,
          noResultsOverlay: customNoDataOverlay ?? customNoTableData,
          ...externalSlots,
        }}
        slotProps={{
          toolbar: {
            customToolbar: customToolbar,
            checkboxSelection: props.checkboxSelection,
            hideToolbarCheckbox: hideToolbarCheckbox,
            saveUserConfig: saveUserConfig,
            handleResetTableView: handleTableReset
              ? () => {
                  handleResetTableView();
                  handleTableReset();
                }
              : (handleResetTableView ?? null),
            isDefaultView: isDefaultView,
            initialState: initialState,
            viewSelect: viewSelect,
            treeData: treeData,
            rowDensityMenu: rowDensityMenu,
            exportMenu: exportMenu,
          },
          ...externalSlotProps,
        }}
        sortingOrder={sortingOrder}
        sortingMode={props.sortingMode ?? "client"}
        {...props}
      />
    </Box>
  );
};

export default MuiDataGrid;
